import tippy, { sticky } from 'tippy.js'
import { computed, ref, onBeforeUpdate } from 'vue'

import userColorMixin from '@/mixins/userColorMixin'
import { bookService } from '@/services'

export default (activeClass = '.editor') => {
  let popup = null
  let insert = null
  let mentionRange = null

  const navigatedMentionIndex = ref(0)
  const filteredMentions = ref([])
  const mentionQuery = ref(null)
  const mentionRef = ref(null)
  const mentionRefs = ref([])

  const hasMentions = computed(() => filteredMentions.value.length)
  const showMentions = computed(() => mentionQuery.value || hasMentions.value)

  // to reset the refs before each update
  onBeforeUpdate(() => {
    mentionRefs.value = []
  })

  function onEnter ({ items, query, range, command, virtualNode, view }) {
    if (view.editable) {
      mentionQuery.value = query
      filteredMentions.value = items
      mentionRange = range
      renderPopup(virtualNode)
      insert = command
    }
  }

  function onChange ({ items, query, range, virtualNode }) {
    if (/^\s/.test(query)) {
      mentionQuery.value = null
      filteredMentions.value = []
    } else {
      mentionQuery.value = query
      filteredMentions.value = items
    }
    mentionRange = range
    navigatedMentionIndex.value = 0
    renderPopup(virtualNode)
  }

  function onExit () {
    mentionQuery.value = null
    filteredMentions.value = []
    mentionRange = null
    navigatedMentionIndex.value = 0
    destroyPopup()
  }

  function onKeyDown ({ event }) {
    if (event.key === 'ArrowUp') {
      upHandler()
      mentionRefs.value[navigatedMentionIndex.value].scrollIntoView(false)
      return true
    }
    if (event.key === 'ArrowDown') {
      downHandler()
      mentionRefs.value[navigatedMentionIndex.value].scrollIntoView(false)
      return true
    }
    if (event.key === 'Enter') {
      const mention = filteredMentions.value[navigatedMentionIndex.value]

      if (mention) {
        enterHandler(mention)
        return true
      } else {
        return false
      }
    }
    return false
  }

  function onFilter (items, query) {
    if (!query) return items
    return items.filter(item => item.title.toLowerCase().match(query.toLowerCase()))
  }
  // navigate to the previous item
  // if it's the first item, navigate to the last one
  function upHandler () {
    navigatedMentionIndex.value = ((navigatedMentionIndex.value + filteredMentions.value.length) - 1) % filteredMentions.value.length
  }
  // navigate to the next item
  // if it's the last item, navigate to the first one
  function downHandler () {
    navigatedMentionIndex.value = (navigatedMentionIndex.value + 1) % filteredMentions.value.length
  }
  function enterHandler (project) {
    selectMention(project)
  }
  // we have to replace our mention text with a mention
  // so it's important to pass also the position of your mention text
  function selectMention (mention, editor) {
    if (mention.label === 'users') {
      const { userColor } = userColorMixin(mention.color)
      insert({
        range: mentionRange,
        attrs: {
          id: mention.id,
          title: mention.firstName,
          label: mention.label,
          color: userColor.value
        }
      })

      if (mention.sendImmediately) {
        const span = document.createElement('span')
        span.innerHTML = editor.getHTML().replace(/(<([^>]+)>)/ig, '\n')

        const index = span.textContent?.lastIndexOf(mention.firstName)

        bookService.mentionMember({
          memberId: mention.id,
          projectId: mention.projectId,
          discussionId: mention.discussionId,
          chatId: mention.chatId,
          bookId: mention.bookId,
          taskId: mention.taskId,
          eventId: mention.eventId,
          message: span.textContent.slice(index, 255 + index)
          // commentId
        })
      }
    } else {
      insert({
        range: mentionRange,
        attrs: {
          href: `/space/${mention.bookId}?page=${mention.id}`,
          title: mention.title,
          label: mention?.label
        }
      })
    }

    if (editor) editor.chain().focus().run()
  }
  // renders a popup with mentionRef
  // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
  function renderPopup (node) {
    if (popup) return

    popup = tippy(activeClass, {
      getReferenceClientRect: node.getBoundingClientRect,
      appendTo: () => document.body,
      interactive: true,
      sticky: true, // make sure position of tippy is updated when content changes
      plugins: [sticky],
      content: mentionRef.value,
      trigger: 'manual', // manual
      showOnCreate: true,
      theme: 'light',
      placement: 'bottom-start',
      inertia: true,
      duration: [400, 200]
    })
  }

  function destroyPopup () {
    if (popup?.length) {
      popup[0].destroy()
      popup = null
    }
  }

  return {
    // data
    navigatedMentionIndex,
    filteredMentions,
    // computed
    hasMentions,
    showMentions,
    // refs
    mentionRef,
    mentionRefs,
    // funcs
    onEnter,
    onKeyDown,
    onChange,
    onExit,
    onFilter,
    selectMention
  }
}
